/**
 * Named-accessor object + membership guards for the `functions.status` D1 column. The column is unconstrained nullable TEXT
 * with NO CHECK, so membership is a RUNTIME question: `resolveFunctionExecutionStatus` is the only sanctioned narrowing, and
 * an unchecked `as FunctionExecutionStatus` on a bare column string is the defect it replaces. The card-action literals keep their own `FunctionCardActionStatus` typing -- that WRITE-side union must not gain the terminal-execution members.
 */

import type { FunctionExecutionStatus } from '@federa/types/ui-function-card';

export const FUNCTION_EXECUTION_STATUS = {
	PENDING: 'pending',
	APPROVED: 'approved',
	DECLINED: 'declined',
	COMPLETED: 'completed',
	FAILED: 'failed',
} as const satisfies Record<string, FunctionExecutionStatus>;

/** Widened to `readonly string[]` so an arbitrary `string` needs no cast. */
const FUNCTION_EXECUTION_STATUS_VALUES: readonly string[] = Object.values(FUNCTION_EXECUTION_STATUS);

/** Type-guard over the status union. @runtime-derived-ssot-helper: membership comes from `FUNCTION_EXECUTION_STATUS_VALUES`, itself `Object.values(FUNCTION_EXECUTION_STATUS)` in this leaf, so guard and SSOT cannot diverge. */
export function isFunctionExecutionStatus(value: unknown): value is FunctionExecutionStatus {
	return typeof value === 'string' && FUNCTION_EXECUTION_STATUS_VALUES.includes(value);
}

/** Absent/null/empty seeds PENDING (the INSERT default). A NON-empty out-of-union value resolves to FAILED: legacy rows carry a stringified success flag, and PENDING would re-offer Confirm/Decline on a finished execution.
 *  @runtime-derived-ssot-helper: both arms resolve to members of the same-file `FUNCTION_EXECUTION_STATUS` SSOT and the
 *  membership test delegates to {@link isFunctionExecutionStatus}, so the narrowing and the SSOT cannot diverge. It is
 *  the only sanctioned narrowing for the unconstrained nullable `functions.status` TEXT column. */
export function resolveFunctionExecutionStatus(value: unknown): FunctionExecutionStatus {
	if (value === null || value === undefined || value === '') return FUNCTION_EXECUTION_STATUS.PENDING;
	return isFunctionExecutionStatus(value) ? value : FUNCTION_EXECUTION_STATUS.FAILED;
}
